import { ExperiencePerformanceTypes } from '@atlassian/ufo-experimental/experience-types';
import {
	type ExperienceData,
	type PageLoadExperienceData,
	type ExperiencePerformanceConfig as PerformanceConfig,
	type ReportedTimings,
} from '@atlassian/ufo-experimental/types';

import { type BundleEvalTimingsConfig, type ResourceTimingsConfig } from '../../../types';
import { type SSRConfig } from '../../types';
import { isInitialPageLoad } from '../utils/is-initial-page-load';

import { getCustomMetrics } from './getCustomMetrics';
import { getInlineResultMetrics } from './getInlineResultMetrics';
import { getLoadMetrics } from './getLoadMetrics';
import { navigationMetrics } from './plugins/navigation-metrics';
import { appTimings } from './plugins/timings/app';
import { bundleEvalTimings } from './plugins/timings/bundle-eval';
import { type ResourceTiming, resourceTimings } from './plugins/timings/resource';

const getPerformanceMetrics = (
	data: ExperienceData | PageLoadExperienceData,
	ssr: SSRConfig | null,
	performanceConfig: PerformanceConfig | null,
) => {
	switch (data.performanceType) {
		case ExperiencePerformanceTypes.PageLoad:
		case ExperiencePerformanceTypes.PageSegmentLoad:
			return getLoadMetrics(data, ssr, performanceConfig);
		case ExperiencePerformanceTypes.InlineResult:
			return getInlineResultMetrics(data, performanceConfig);
		case ExperiencePerformanceTypes.Custom:
			return getCustomMetrics(data, performanceConfig);
	}
};

export const getPerformancePayload = (
	data: ExperienceData | PageLoadExperienceData,
	ssr: SSRConfig | null,
	bundleEvalConfig: BundleEvalTimingsConfig | null,
	resourceConfig: ResourceTimingsConfig | null,
	performanceConfig: PerformanceConfig | null,
): {
	'metrics:navigation'?:
		| {
				connectEnd: number;
				connectStart: number;
				decodedBodySize: number;
				domainLookupEnd: number;
				domainLookupStart: number;
				encodedBodySize: number;
				fetchStart: number;
				redirectCount: number;
				redirectEnd: number;
				redirectStart: number;
				requestStart: number;
				responseEnd: number;
				responseStart: number;
				secureConnectionStart: number;
				transferSize: number;
				type: NavigationTimingType;
				unloadEventEnd: number;
				unloadEventStart: number;
				workerStart: number;
		  }
		| undefined;
	'timings:app'?: any;
	'timings:bundleEval'?: ReportedTimings | undefined;
	'timings:resource'?:
		| {
				[key: string]: ResourceTiming;
		  }
		| undefined;
} => {
	return {
		...getPerformanceMetrics(data, ssr, performanceConfig),
		...resourceTimings(data, resourceConfig),
		...bundleEvalTimings(data, bundleEvalConfig),
		...appTimings(data, ssr),
		...(isInitialPageLoad(data) ? navigationMetrics() : null),
	};
};
